/*
Shared, pure nav-tree model for the doc system.

Like routing.ts, this is imported by BOTH the build-time generator
(src/doc-system/site/generate-site.ts) and the runtime component
(src/doc-browser.ts), so static and hydrated navigation always agree. Every
function here is a pure, order-independent function of the docs array.

A doc may declare a `parent` (a name or slug) in its metadata; docs sharing a
parent form a nav section, nested arbitrarily deep. Parents are expected to
exist as real docs by the time the tree is built (the build auto-creates any
missing section docs first), so this module never synthesizes.
*/

import { resolveParent } from './routing'

export interface NavDoc {
  filename: string
  title: string
  pin?: 'top' | 'bottom'
  /** sub-order within a pin bucket (lower first); sections use this for order */
  order?: number
  /** parent doc name or slug; resolved via resolveParent */
  parent?: string
}

export interface NavNode<T extends NavDoc = NavDoc> {
  doc: T
  slug: string
  children: NavNode<T>[]
  depth: number
}

/**
 * Total-order sort key: pin bucket (top<none<bottom), then `order` (default
 * 500), then title, then filename. The filename tiebreak guarantees a stable
 * total order even for equal titles — required so the generated TOC blocks are
 * idempotent (no build-to-build churn).
 */
export function navSortKey(doc: NavDoc): string {
  const bucket = doc.pin === 'top' ? 'A' : doc.pin === 'bottom' ? 'Z' : 'M'
  const order = String(doc.order ?? 500).padStart(4, '0')
  return bucket + order + (doc.title || '').toLocaleLowerCase() + ' ' + doc.filename
}

export function pinnedSort(a: NavDoc, b: NavDoc): number {
  const ak = navSortKey(a)
  const bk = navSortKey(b)
  return ak > bk ? 1 : bk > ak ? -1 : 0
}

/**
 * Build the forest of top-level nav nodes. Children are grouped under their
 * resolved parent and each sibling group is sorted by pinnedSort. A doc whose
 * parent can't be resolved (or which would be its own ancestor) becomes a root.
 */
export function buildNavTree<T extends NavDoc>(
  docs: T[],
  slugMap: Record<string, string>
): NavNode<T>[] {
  const nodes = new Map<string, NavNode<T>>()
  for (const doc of docs) {
    nodes.set(doc.filename, {
      doc,
      slug: slugMap[doc.filename] ?? '',
      children: [],
      depth: 0,
    })
  }

  // Resolve parents, guarding against missing parents and parent cycles.
  const parentOf = new Map<string, string>()
  for (const doc of docs) {
    let pf = doc.parent ? resolveParent(doc.parent, docs, slugMap) : ''
    if (pf && (pf === doc.filename || !nodes.has(pf) || createsCycle(doc.filename, pf, docs, slugMap))) {
      pf = ''
    }
    parentOf.set(doc.filename, pf)
  }

  const roots: NavNode<T>[] = []
  for (const doc of docs) {
    const pf = parentOf.get(doc.filename) || ''
    const node = nodes.get(doc.filename)!
    if (pf) nodes.get(pf)!.children.push(node)
    else roots.push(node)
  }

  const sortNodes = (arr: NavNode<T>[], depth: number): void => {
    arr.sort((x, y) => pinnedSort(x.doc, y.doc))
    for (const n of arr) {
      n.depth = depth
      sortNodes(n.children, depth + 1)
    }
  }
  sortNodes(roots, 0)
  return roots
}

/** Walk parent links from `start` to detect whether linking child->parent loops. */
function createsCycle(
  child: string,
  parent: string,
  docs: Array<{ filename: string; title?: string; parent?: string }>,
  slugMap: Record<string, string>
): boolean {
  const byFilename = new Map(docs.map((d) => [d.filename, d]))
  let cursor: string | undefined = parent
  const seen = new Set<string>([child])
  while (cursor) {
    if (seen.has(cursor)) return true
    seen.add(cursor)
    const doc = byFilename.get(cursor)
    cursor = doc?.parent ? resolveParent(doc.parent, docs, slugMap) : ''
  }
  return false
}

/**
 * Filenames of the nodes whose <details> should be open: every node that has
 * children and whose subtree contains `currentFilename`.
 */
export function navOpenPath<T extends NavDoc>(
  roots: NavNode<T>[],
  currentFilename: string
): Set<string> {
  const open = new Set<string>()
  const containsCurrent = (node: NavNode<T>): boolean => {
    let found = node.doc.filename === currentFilename
    for (const child of node.children) {
      if (containsCurrent(child)) found = true
    }
    if (found && node.children.length > 0) open.add(node.doc.filename)
    return found
  }
  for (const root of roots) containsCurrent(root)
  return open
}
